"use client";

import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, MapPin, Clock, Phone, ArrowRight, Check, Loader2 } from "lucide-react";
import { DEV_RESOURCES, EXTERNAL_LINKS } from "@/lib/constants";

const contactInfo = [
  {
    icon: Mail,
    title: "Email",
    content: EXTERNAL_LINKS.email,
    href: `mailto:${EXTERNAL_LINKS.email}`,
  },
  {
    icon: Phone,
    title: "Phone",
    content: "+49 (0) 8164 12345",
    href: "tel:+49816412345",
  },
  {
    icon: Clock,
    title: "Response Time",
    content: "Within 24 hours",
    href: null,
  },
  {
    icon: MapPin,
    title: "Headquarters",
    content: "Wang, Germany",
    href: null,
  },
];

export default function Contact() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    company: "",
    useCase: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formState.name.trim()) newErrors.name = "Name is required";
    if (!formState.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formState.company.trim()) newErrors.company = "Company is required";
    if (!formState.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial" />
          <div className="container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-[var(--white-3)] border border-[var(--border-subtle)] rounded-full">
                <span className="text-sm font-medium text-fg-muted">Contact Us</span>
              </div>
              <h1 className="text-hero text-fg mb-6">
                Talk to our
                <span className="text-accent"> team</span>
              </h1>
              <p className="text-body-large">
                Whether you need a technical deep-dive or a quick overview,
                we respond within 24 hours.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Grid */}
        <section className="py-20 border-t border-[var(--border-subtle)]">
          <div className="container">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Contact Info Cards */}
              <div className="lg:col-span-1 order-2 lg:order-1 space-y-4">
                {contactInfo.map((info) => (
                  <div
                    key={info.title}
                    className="p-6 bg-bg-elevated border border-[var(--border-subtle)] rounded-2xl group hover:border-accent/20 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-fg-subtle mb-1">{info.title}</h3>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="text-fg hover:text-accent transition-colors py-2 inline-block"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-fg">{info.content}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Developer Resources */}
                <div className="p-6 bg-bg-elevated border border-[var(--border-subtle)] rounded-2xl mt-8">
                  <h3 className="font-semibold text-fg mb-4">Developer Resources</h3>
                  <div className="space-y-3">
                    {DEV_RESOURCES.map((resource) => (
                      <a
                        key={resource.name}
                        href={resource.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-fg-muted hover:text-accent transition-colors py-2"
                      >
                        {resource.showStatusDot ? (
                          <span className="w-2 h-2 bg-success rounded-full" />
                        ) : (
                          <ArrowRight className="w-4 h-4" />
                        )}
                        {resource.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2 order-1 lg:order-2">
                <div className="p-8 bg-bg-elevated border border-[var(--border-subtle)] rounded-2xl">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-8 h-8 text-success" />
                      </div>
                      <h2 className="text-title text-fg mb-4">Message Sent!</h2>
                      <p className="text-fg-muted mb-8">
                        Thank you for reaching out. We&apos;ll get back to you within 24 hours.
                      </p>
                      <button
                        onClick={() => {
                          setIsSubmitted(false);
                          setFormState({ name: "", email: "", company: "", useCase: "", message: "" });
                        }}
                        className="btn-secondary"
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-xl font-semibold text-fg mb-2">Book a Demo</h2>
                      <p className="text-fg-subtle mb-8">
                        Fill out the form below and we&apos;ll be in touch shortly.
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-fg mb-2">
                              Full Name <span className="text-accent">*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formState.name}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-bg border rounded-xl text-fg placeholder-fg-subtle focus:outline-none focus:border-accent transition-colors ${
                                errors.name ? "border-error" : "border-[var(--border-subtle)]"
                              }`}
                              placeholder="John Doe"
                            />
                            {errors.name && (
                              <p className="mt-1 text-sm text-error">{errors.name}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-fg mb-2">
                              Email Address <span className="text-accent">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formState.email}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 bg-bg border rounded-xl text-fg placeholder-fg-subtle focus:outline-none focus:border-accent transition-colors ${
                                errors.email ? "border-error" : "border-[var(--border-subtle)]"
                              }`}
                              placeholder="john@company.com"
                            />
                            {errors.email && (
                              <p className="mt-1 text-sm text-error">{errors.email}</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label htmlFor="company" className="block text-sm font-medium text-fg mb-2">
                            Company <span className="text-accent">*</span>
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formState.company}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 bg-bg border rounded-xl text-fg placeholder-fg-subtle focus:outline-none focus:border-accent transition-colors ${
                              errors.company ? "border-error" : "border-[var(--border-subtle)]"
                            }`}
                            placeholder="Your company name"
                          />
                          {errors.company && (
                            <p className="mt-1 text-sm text-error">{errors.company}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="useCase" className="block text-sm font-medium text-fg mb-2">
                            What are you looking for?
                          </label>
                          <select
                            id="useCase"
                            name="useCase"
                            value={formState.useCase}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-bg border border-[var(--border-subtle)] rounded-xl text-fg focus:outline-none focus:border-accent transition-colors appearance-none"
                          >
                            <option value="" className="text-fg-subtle">Select a use case</option>
                            <option value="shopping-assistant">AI Shopping Assistant</option>
                            <option value="semantic-search">Semantic Search</option>
                            <option value="b2b-orders">B2B Order Processing</option>
                            <option value="custom-integration">Custom Integration</option>
                            <option value="other">Something Else</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="message" className="block text-sm font-medium text-fg mb-2">
                            Message <span className="text-accent">*</span>
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows={5}
                            value={formState.message}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 bg-bg border rounded-xl text-fg placeholder-fg-subtle focus:outline-none focus:border-accent transition-colors resize-none ${
                              errors.message ? "border-error" : "border-[var(--border-subtle)]"
                            }`}
                            placeholder="Tell us about your e-commerce platform and how we can help..."
                          />
                          {errors.message && (
                            <p className="mt-1 text-sm text-error">{errors.message}</p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              Send Message
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </button>

                        <p className="text-xs text-fg-subtle text-center">
                          By submitting this form, you agree to our{" "}
                          <a href="/privacy" className="text-accent hover:underline">
                            Privacy Policy
                          </a>
                          .
                        </p>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
